// RestoreHistoryTab - History of all restores and imports
import { useState, useEffect, useCallback } from "react";
import { backupAPI } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RotateCcw, CheckCircle, XCircle, Loader2, Clock, RefreshCw } from "lucide-react";
import { formatRelativeTime } from "@/lib/utils";

export default function RestoreHistoryTab() {
  const [restores, setRestores] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const res = await backupAPI.listAllRestores(50).catch(() => ({ data: [] }));
      setRestores(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    loadData();
    const iv = setInterval(loadData, 15000);
    return () => clearInterval(iv);
  }, [loadData]);

  const getStatusBadge = (status) => {
    const m = {
      completed: { label: "Terminé", icon: CheckCircle, color: "text-green-600", variant: "default" },
      running: { label: "En cours", icon: Loader2, color: "text-blue-600", variant: "secondary" },
      failed: { label: "Échoué", icon: XCircle, color: "text-red-600", variant: "destructive" },
    };
    const c = m[status] || { label: status, icon: Clock, color: "", variant: "outline" };
    const Icon = c.icon;
    return (
      <Badge variant={c.variant} className={`gap-1 ${c.color}`}>
        <Icon className={`h-3 w-3 ${status === 'running' ? 'animate-spin' : ''}`} />
        {c.label}
      </Badge>
    );
  };

  const getTypeBadge = (type) => {
    const m = {
      full_db_restore: { label: "Restauration BD", color: "bg-purple-100 text-purple-700 border-purple-200" },
      workspace_import: { label: "Import Workspace", color: "bg-green-100 text-green-700 border-green-200" },
      workspace: { label: "Restore Workspace", color: "bg-blue-100 text-blue-700 border-blue-200" },
      full: { label: "Restore Complet", color: "bg-orange-100 text-orange-700 border-orange-200" },
    };
    const c = m[type] || { label: type || "Restauration", color: "" };
    return <Badge variant="outline" className={c.color}>{c.label}</Badge>;
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5" /> Historique des restaurations & imports
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={loadData}><RefreshCw className="h-4 w-4" /></Button>
        </CardHeader>
        <CardContent>
          {restores.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <RotateCcw className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>Aucune restauration ou import effectué</p>
            </div>
          ) : (
            <div className="space-y-3">
              {restores.map(rj => (
                <div key={rj.id} className="flex items-center gap-3 p-4 rounded-lg border bg-card hover:bg-muted/30 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {getTypeBadge(rj.restore_type)}
                      {getStatusBadge(rj.status)}
                    </div>
                    <p className="text-sm mt-1">
                      {rj.restore_type === "workspace_import"
                        ? `Import depuis backup ${rj.backup_job_id?.slice(0, 8) || "?"}`
                        : `Restauration depuis backup ${rj.backup_job_id?.slice(0, 8) || "?"}`}
                      {rj.workspace_id && ` (workspace ${rj.workspace_id.slice(0, 8)})`}
                    </p>
                    <div className="flex gap-4 mt-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{rj.started_at ? formatRelativeTime(rj.started_at) : "-"}</span>
                      {rj.initiated_by_name && <span>Par: {rj.initiated_by_name}</span>}
                    </div>
                    {rj.error && <p className="text-xs text-destructive mt-1 truncate">{rj.error}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
